import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { useToast } from '../context/ToastContext';
import { isKioskModeEnabled } from '../lib/kiosk-stability';

// Types
interface Member {
  id: string;
  name: string;
  type: 'adult' | 'teen' | 'child';
  color: string;
  userId?: string | null;
  searchKeywords?: string[];
}

interface ConnectedAccountsInfo {
  google: { count: number; max: number };
  microsoft: { count: number; max: number };
}

interface ConnectedAccountInfo {
  email: string;
  name: string | null;
  needsReconnect?: boolean;
  reconnectReason?: string | null;
}

interface ConnectedAccountsDetailData {
  google: {
    accounts: ConnectedAccountInfo[];
    count: number;
    max: number;
  };
  microsoft: {
    accounts: ConnectedAccountInfo[];
    count: number;
    max: number;
  };
}

interface CalendarEvent {
  id: string;
  sourceId: string;
  title: string;
  description: string | null;
  startTime: string;
  endTime: string;
  allDay: boolean;
  location: string | null;
  member: Member | null;
}

interface EventsRangeResponse {
  members: Member[];
  eventsByDate: Record<string, Record<string, CalendarEvent[]>>;
}

interface CalendarSource {
  id: string;
  memberId: string;
  type: 'google' | 'microsoft' | 'ics';
  name: string;
  externalId: string | null;
  sourceUrl: string | null;
  refreshInterval: number;
  lastSynced: string | null;
  isActive: boolean;
  accountEmail: string | null;
}

interface AvailableCalendar {
  id: string;
  name: string;
  primary?: boolean;
  backgroundColor?: string;
  color?: string;
  // Account context (added for multi-account support)
  accountId: string;
  accountEmail: string;
  accountName: string | null;
}

interface CalendarListResponse {
  calendars: AvailableCalendar[];
  errors?: Array<{
    accountEmail: string;
    needsReconnect: boolean;
    message: string;
  }>;
}

// Events hooks
export function useEvents(start?: string, end?: string, memberIds?: string[]) {
  const params = new URLSearchParams();
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  if (memberIds?.length) params.set('memberIds', memberIds.join(','));

  return useQuery({
    queryKey: ['events', start, end, memberIds],
    queryFn: () => api.get<CalendarEvent[]>(`/events?${params.toString()}`),
  });
}

export function useTodayEvents() {
  return useQuery({
    queryKey: ['events', 'today'],
    queryFn: () => api.get<CalendarEvent[]>('/events/today'),
  });
}

export function useEventsRange(days: number, pastDays: number = 0) {
  // In kiosk mode, shrink the event window to reduce heap pressure on the
  // 256 MB Android WebView cap.  The 395-day window allocates a large
  // eventsByDate object that is completely replaced on every 10-min
  // refetch; capping to 7 days forward / 14 days back cuts the object to
  // ~5% of the original size, and a short gcTime ensures the superseded
  // snapshot is freed promptly by TanStack Query's garbage collector.
  const kiosk = isKioskModeEnabled();
  // Known limitation: a 7-day forward window means month view would only
  // populate ~the first week of events. This is acceptable because the kiosk
  // smart-default view is always 'school' or 'week', never 'month'.
  const effectiveDays = kiosk ? 7 : days;
  const effectivePastDays = kiosk ? 14 : pastDays;
  const pastParam = effectivePastDays > 0 ? `?pastDays=${effectivePastDays}` : '';
  return useQuery({
    queryKey: ['events', 'range', effectiveDays, effectivePastDays],
    queryFn: () => api.get<EventsRangeResponse>(`/events/range/${effectiveDays}${pastParam}`),
    staleTime: 10 * 60 * 1000, // Consider data fresh for 10 minutes
    refetchInterval: 10 * 60 * 1000, // Refetch every 10 minutes
    // Kiosk: free superseded snapshots quickly so GC can reclaim the heap.
    gcTime: kiosk ? 30 * 1000 : 5 * 60 * 1000,
  });
}

// Family hooks
interface Family {
  id: string;
  name: string;
  homeAddress: string | null;
  homeAddressPlaceId: string | null;
  homeAddressLat: string | null;
  homeAddressLng: string | null;
  timezone: string;
  settings: Record<string, unknown>;
}

export function useFamily() {
  return useQuery({
    queryKey: ['family'],
    queryFn: () => api.get<Family>('/family'),
  });
}

export function useUpdateFamily() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      name?: string;
      homeAddress?: string | null;
      homeAddressPlaceId?: string | null;
      homeAddressLat?: number | null;
      homeAddressLng?: number | null;
      timezone?: string;
    }) => api.patch<Family>('/family', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family'] });
    },
  });
}

// Family members hooks
export function useFamilyMembers() {
  return useQuery({
    queryKey: ['members'],
    queryFn: () => api.get<Member[]>('/members'),
  });
}

export function useConnectedAccounts(memberId: string | null | undefined) {
  return useQuery({
    queryKey: ['connected-accounts', memberId],
    queryFn: () => api.get<ConnectedAccountsInfo>(`/members/${memberId}/connected-accounts`),
    enabled: !!memberId,
  });
}

export function useConnectedAccountsDetail(memberId: string | null | undefined) {
  return useQuery({
    queryKey: ['connected-accounts-detail', memberId],
    queryFn: () => api.get<ConnectedAccountsDetailData>(`/members/${memberId}/connected-accounts/details`),
    enabled: !!memberId,
  });
}

export function useUpdateMemberKeywords() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, keywords }: { memberId: string; keywords: string[] }) =>
      api.patch<Member>(`/members/${memberId}`, { searchKeywords: keywords }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
      queryClient.invalidateQueries({ queryKey: ['events'] }); // Re-fetch events with new attribution
    },
  });
}

// Calendar sources hooks
export function useCalendarSources(memberId: string) {
  return useQuery({
    queryKey: ['sources', memberId],
    queryFn: () => api.get<CalendarSource[]>(`/members/${memberId}/sources`),
    enabled: !!memberId,
  });
}

export function useGoogleCalendars(memberId: string | null) {
  return useQuery({
    queryKey: ['calendars', 'google', memberId],
    queryFn: () => api.get<CalendarListResponse>(`/sources/google/calendars?memberId=${memberId}`),
    enabled: !!memberId,
  });
}

export function useMicrosoftCalendars(memberId: string | null) {
  return useQuery({
    queryKey: ['calendars', 'microsoft', memberId],
    queryFn: () => api.get<CalendarListResponse>(`/sources/microsoft/calendars?memberId=${memberId}`),
    enabled: !!memberId,
  });
}

export function useAddCalendarSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      memberId: string;
      type: 'google' | 'microsoft' | 'ics';
      name: string;
      externalId?: string;
      sourceUrl?: string;
    }) => api.post<CalendarSource>('/sources', data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sources', variables.memberId] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useSyncCalendarSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sourceId, force = false }: { sourceId: string; force?: boolean }) =>
      api.post<{ syncedEvents: number }>(`/sources/${sourceId}/sync${force ? '?force=true' : ''}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      // Also refresh sources/family data to update "Synced X minutes ago" display
      queryClient.invalidateQueries({ queryKey: ['sources'] });
      queryClient.invalidateQueries({ queryKey: ['family'] });
    },
  });
}

export function useDeleteCalendarSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sourceId: string) => api.delete(`/sources/${sourceId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sources'] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

// Recurrence config type
interface RecurrenceConfig {
  daysOfWeek?: number[];
  dayOfMonth?: number;
  weekOfMonth?: number;
  dayOfWeek?: number;
  interval?: number;
  endDate?: string;
  occurrenceCount?: number;
  exceptionDates?: string[];
  startDate?: string;
}

// Chores types
interface Chore {
  id: string;
  familyId: string;
  title: string;
  description: string | null;
  recurrence: 'daily' | 'weekly' | 'monthly' | 'once';
  recurrenceConfig: RecurrenceConfig | null;
  points: number;
  category: string | null;
  dueTime: string | null;
  isActive: boolean;
  assignments: {
    id: string;
    choreId: string;
    memberId: string;
    member: Member;
  }[];
}

interface TodayChore extends Chore {
  assignedMembers: (Member & { completed: boolean; completedAt: string | null })[];
  allCompleted: boolean;
  completedByMember?: {
    memberId: string;
    memberName: string;
    memberColor: string;
  };
}

interface ChoreCompletion {
  id: string;
  choreId: string;
  memberId: string;
  instanceDate: string;
  completedAt: string;
  pointsEarned: number;
  chore: Chore;
  member: Member;
}

interface LeaderboardEntry {
  member: Member;
  totalPoints: number;
  completionCount: number;
}

// Chores hooks
export function useChores() {
  return useQuery({
    queryKey: ['chores'],
    queryFn: () => api.get<Chore[]>('/chores'),
  });
}

export function useTodayChores() {
  // Check if kiosk mode is active - use more conservative refetch interval
  const isKiosk = typeof window !== 'undefined' && 
    (window.location.search.includes('kiosk=1') || 
     window.location.search.includes('kiosk=true') || 
     localStorage.getItem('kiosk_mode_enabled') === 'true');
  
  return useQuery({
    queryKey: ['chores', 'today'],
    queryFn: () => api.get<TodayChore[]>('/chores/today'),
    // Kiosk mode: refetch every 5 minutes to reduce load
    // Normal mode: refetch every minute for better responsiveness
    refetchInterval: isKiosk ? 5 * 60 * 1000 : 60 * 1000,
  });
}

export function useChoresLeaderboard(period?: 'week' | 'month' | 'all') {
  const params = period ? `?period=${period}` : '';
  return useQuery({
    queryKey: ['chores', 'leaderboard', period],
    queryFn: () => api.get<LeaderboardEntry[]>(`/chores/leaderboard${params}`),
  });
}

export function useChoreHistory(options?: { memberId?: string; startDate?: string; endDate?: string }) {
  const params = new URLSearchParams();
  if (options?.memberId) params.set('memberId', options.memberId);
  if (options?.startDate) params.set('startDate', options.startDate);
  if (options?.endDate) params.set('endDate', options.endDate);
  const queryStr = params.toString();

  return useQuery({
    queryKey: ['chores', 'history', options],
    queryFn: () => api.get<ChoreCompletion[]>(`/chores/history${queryStr ? `?${queryStr}` : ''}`),
  });
}

export function useCreateChore() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      title: string;
      description?: string;
      recurrence: 'daily' | 'weekly' | 'monthly' | 'once';
      recurrenceConfig?: RecurrenceConfig;
      points?: number;
      category?: string;
      dueTime?: string;
      assignedMemberIds?: string[];
    }) => api.post<Chore>('/chores', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chores'] });
    },
  });
}

export function useUpdateChore() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, assignedMemberIds, ...data }: {
      id: string;
      title?: string;
      description?: string;
      recurrence?: 'daily' | 'weekly' | 'monthly' | 'once';
      recurrenceConfig?: object;
      points?: number;
      category?: string;
      dueTime?: string;
      isActive?: boolean;
      assignedMemberIds?: string[];
    }) => {
      // Update chore fields
      const chore = await api.patch<Chore>(`/chores/${id}`, data);

      // Update assignments if provided
      if (assignedMemberIds !== undefined) {
        await api.put(`/chores/${id}/assignments`, { memberIds: assignedMemberIds });
      }

      return chore;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chores'] });
    },
  });
}

export function useDeleteChore() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/chores/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chores'] });
    },
  });
}

export function useCompleteChore() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ choreId, memberId, date }: { choreId: string; memberId: string; date?: string }) =>
      api.post<{ completion: ChoreCompletion; pointsEarned: number }>(`/chores/${choreId}/complete`, { memberId, date }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chores'] });
    },
  });
}

export function useUndoChoreCompletion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ choreId, memberId, date }: { choreId: string; memberId: string; date?: string }) =>
      api.delete(`/chores/${choreId}/complete`, { memberId, date }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chores'] });
    },
  });
}

// Lists types
interface ListItem {
  id: string;
  listId: string;
  title: string;
  notes: string | null;
  checked: boolean;
  order: number;
  externalId?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

interface List {
  id: string;
  familyId: string;
  name: string;
  type: 'groceries' | 'packing' | 'watch' | 'custom';
  color: string | null;
  icon: string | null;
  items: ListItem[];
  itemCount: number;
  checkedCount: number;
}

// Lists hooks
export function useLists() {
  return useQuery({
    queryKey: ['lists'],
    queryFn: () => api.get<List[]>('/lists'),
    staleTime: 24 * 60 * 60 * 1000, // Consider fresh for 24 hours
    gcTime: 24 * 60 * 60 * 1000, // Keep in cache for 24 hours
    refetchOnMount: false, // Don't refetch if data is fresh
    refetchOnWindowFocus: true, // Do refetch on window focus for sync
  });
}

export function useList(id: string) {
  return useQuery({
    queryKey: ['lists', id],
    queryFn: () => api.get<List>(`/lists/${id}`),
    enabled: !!id,
    staleTime: 24 * 60 * 60 * 1000, // Consider fresh for 24 hours
    gcTime: 24 * 60 * 60 * 1000, // Keep in cache for 24 hours
    refetchOnMount: false, // Don't refetch if data is fresh
    refetchOnWindowFocus: true, // Do refetch on window focus for sync
  });
}

export function useCreateList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      name: string;
      type?: 'groceries' | 'packing' | 'watch' | 'custom';
      color?: string;
      icon?: string;
    }) => api.post<List>('/lists', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lists'] });
    },
  });
}

export function useUpdateList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: {
      id: string;
      name?: string;
      type?: 'groceries' | 'packing' | 'watch' | 'custom';
      color?: string;
      icon?: string;
    }) => api.patch<List>(`/lists/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lists'] });
    },
  });
}

export function useDeleteList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/lists/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lists'] });
    },
  });
}

export function useAddListItem() {
  const queryClient = useQueryClient();
  const { error } = useToast();

  return useMutation({
    mutationFn: ({ listId, title, notes }: { listId: string; title: string; notes?: string }) =>
      api.post<ListItem>(`/lists/${listId}/items`, { title, notes }),
    onMutate: async ({ listId, title, notes }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['lists'] });

      // Snapshot previous value
      const previousLists = queryClient.getQueryData<List[]>(['lists']);

      // Create temp item for optimistic update
      const tempItem: ListItem = {
        id: `temp-${Date.now()}`,
        listId,
        title,
        notes: notes || null,
        checked: false,
        order: Date.now(),
        externalId: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Optimistically add the new item to all-lists cache
      queryClient.setQueryData<List[]>(['lists'], (old) => {
        if (!old) return old;
        return old.map((list) =>
          list.id === listId
            ? {
                ...list,
                items: [...list.items, tempItem],
                itemCount: list.itemCount + 1,
              }
            : list
        );
      });

      // ALSO update the individual list cache
      const previousList = queryClient.getQueryData<List>(['lists', listId]);
      queryClient.setQueryData<List>(['lists', listId], (old) => {
        if (!old) return old;
        return {
          ...old,
          items: [...old.items, tempItem],
          itemCount: old.itemCount + 1,
        };
      });

      return { previousLists, previousList, tempItem };
    },
    onError: (_err, variables, context) => {
      // Rollback on error
      if (context?.previousLists) {
        queryClient.setQueryData<List[]>(['lists'], context.previousLists);
      }
      if (context?.previousList) {
        queryClient.setQueryData<List>(['lists', variables.listId], context.previousList);
      }
      error('Failed to add item - changes reverted');
    },
    onSuccess: (newItem, variables, context) => {
      // Replace temp item with real item in both caches
      if (!context?.tempItem) return;

      // Update all-lists cache
      queryClient.setQueryData<List[]>(['lists'], (old) => {
        if (!old) return old;
        return old.map((list) =>
          list.id === variables.listId
            ? {
                ...list,
                items: list.items.map((item) =>
                  item.id === context.tempItem!.id ? newItem : item
                ),
              }
            : list
        );
      });

      // Update individual list cache
      queryClient.setQueryData<List>(['lists', variables.listId], (old) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.map((item) =>
            item.id === context.tempItem!.id ? newItem : item
          ),
        };
      });
    },
    onSettled: (_, __, variables) => {
      // Always refetch to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ['lists', variables.listId] });
      queryClient.invalidateQueries({ queryKey: ['lists'] });
    },
  });
}

export function useUpdateListItem() {
  const queryClient = useQueryClient();
  const { error } = useToast();

  return useMutation({
    mutationFn: ({ id, ...data }: {
      id: string;
      title?: string;
      notes?: string;
      checked?: boolean;
    }) => api.patch<ListItem>(`/lists/items/${id}`, data),
    onMutate: async ({ id, ...data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['lists'] });

      // Snapshot previous values
      const previousLists = queryClient.getQueryData<List[]>(['lists']);

      // Find which list this item belongs to
      const lists = queryClient.getQueryData<List[]>(['lists']);
      const parentList = lists?.find((list) =>
        list.items.some((item) => item.id === id)
      );
      const previousList = parentList ? queryClient.getQueryData<List>(['lists', parentList.id]) : null;

      // Optimistically update the all-lists cache
      queryClient.setQueryData<List[]>(['lists'], (old) => {
        if (!old) return old;
        return old.map((list) => ({
          ...list,
          items: list.items.map((item) =>
            item.id === id ? { ...item, ...data } : item
          ),
          // Update checked count if checked state changed
          checkedCount: data.checked !== undefined
            ? list.items.reduce((count, item) =>
                count + (item.id === id ? (data.checked ? 1 : 0) : (item.checked ? 1 : 0)), 0)
            : list.checkedCount,
        }));
      });

      // ALSO update the individual list cache
      if (parentList) {
        queryClient.setQueryData<List>(['lists', parentList.id], (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.map((item) =>
              item.id === id ? { ...item, ...data } : item
            ),
            // Update checked count if checked state changed
            checkedCount: data.checked !== undefined
              ? old.items.reduce((count, item) =>
                  count + (item.id === id ? (data.checked ? 1 : 0) : (item.checked ? 1 : 0)), 0)
              : old.checkedCount,
          };
        });
      }

      return { previousLists, previousList: previousList || undefined, parentListId: parentList?.id };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousLists) {
        queryClient.setQueryData<List[]>(['lists'], context.previousLists);
      }
      if (context?.previousList && context?.parentListId) {
        queryClient.setQueryData<List>(['lists', context.parentListId], context.previousList);
      }
      // Show error toast - sync failed and changes were reverted
      error('Failed to sync - changes reverted');
    },
    onSettled: () => {
      // Always refetch to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ['lists'] });
    },
  });
}

export function useDeleteListItem() {
  const queryClient = useQueryClient();
  const { error } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/lists/items/${id}`),
    onMutate: async (id: string) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['lists'] });

      // Snapshot previous value
      const previousLists = queryClient.getQueryData<List[]>(['lists']);

      // Find which list this item belongs to
      const lists = queryClient.getQueryData<List[]>(['lists']);
      const parentList = lists?.find((list) =>
        list.items.some((item) => item.id === id)
      );
      const previousList = parentList ? queryClient.getQueryData<List>(['lists', parentList.id]) : null;

      // Optimistically remove the item from all-lists cache
      queryClient.setQueryData<List[]>(['lists'], (old) => {
        if (!old) return old;
        return old.map((list) => {
          const wasChecked = list.items.find((item) => item.id === id)?.checked || false;
          return {
            ...list,
            items: list.items.filter((item) => item.id !== id),
            itemCount: list.itemCount - 1,
            checkedCount: wasChecked ? list.checkedCount - 1 : list.checkedCount,
          };
        });
      });

      // ALSO update the individual list cache
      if (parentList) {
        queryClient.setQueryData<List>(['lists', parentList.id], (old) => {
          if (!old) return old;
          const wasChecked = old.items.find((item) => item.id === id)?.checked || false;
          return {
            ...old,
            items: old.items.filter((item) => item.id !== id),
            itemCount: old.itemCount - 1,
            checkedCount: wasChecked ? old.checkedCount - 1 : old.checkedCount,
          };
        });
      }

      return { previousLists, previousList: previousList || undefined, parentListId: parentList?.id };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousLists) {
        queryClient.setQueryData<List[]>(['lists'], context.previousLists);
      }
      if (context?.previousList && context?.parentListId) {
        queryClient.setQueryData<List>(['lists', context.parentListId], context.previousList);
      }
      error('Failed to delete item - changes reverted');
    },
    onSettled: () => {
      // Always refetch to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ['lists'] });
    },
  });
}

export function useClearCheckedItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (listId: string) => api.delete(`/lists/${listId}/checked`),
    onMutate: async (listId: string) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['lists'] });

      // Snapshot previous values
      const previousLists = queryClient.getQueryData<List[]>(['lists']);
      const previousList = queryClient.getQueryData<List>(['lists', listId]);

      // Optimistically remove checked items from all-lists cache
      queryClient.setQueryData<List[]>(['lists'], (old) => {
        if (!old) return old;
        return old.map((list) => {
          if (list.id !== listId) return list;
          const uncheckedItems = list.items.filter((item) => !item.checked);
          return {
            ...list,
            items: uncheckedItems,
            itemCount: uncheckedItems.length,
            checkedCount: 0,
          };
        });
      });

      // ALSO update the individual list cache
      queryClient.setQueryData<List>(['lists', listId], (old) => {
        if (!old) return old;
        const uncheckedItems = old.items.filter((item) => !item.checked);
        return {
          ...old,
          items: uncheckedItems,
          itemCount: uncheckedItems.length,
          checkedCount: 0,
        };
      });

      return { previousLists, previousList };
    },
    onError: (_err, variables, context) => {
      // Rollback on error
      if (context?.previousLists) {
        queryClient.setQueryData<List[]>(['lists'], context.previousLists);
      }
      if (context?.previousList) {
        queryClient.setQueryData<List>(['lists', variables], context.previousList);
      }
    },
    onSettled: (_, __, listId) => {
      // Always refetch to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ['lists', listId] });
      queryClient.invalidateQueries({ queryKey: ['lists'] });
    },
  });
}

// Google Keep types
interface KeepStatus {
  connected: boolean;
  connectedAt: string | null;
}

interface KeepList {
  id: string;
  title: string;
  item_count: number;
  checked_count: number;
  color: string | null;
  archived: boolean;
  trashed: boolean;
  synced: boolean;
  localListId: string | null;
}

interface KeepSource {
  id: string;
  userId: string;
  familyId: string;
  keepListId: string;
  localListId: string | null;
  name: string;
  syncEnabled: boolean;
  syncDirection: 'bidirectional' | 'keep_to_local' | 'local_to_keep';
  lastSynced: string | null;
  lastSyncError: string | null;
  localList?: List;
}

// Google Keep hooks
export function useKeepStatus() {
  return useQuery({
    queryKey: ['keep', 'status'],
    queryFn: () => api.get<KeepStatus>('/keep/status'),
  });
}

export function useConnectKeep() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { email: string; password: string }) =>
      api.post<{ connected: boolean; email: string }>('/keep/connect', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['keep'] });
    },
  });
}

export function useConnectKeepOAuth() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { email: string; oauthToken: string }) =>
      api.post<{ connected: boolean; email: string }>('/keep/connect-oauth', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['keep'] });
    },
  });
}

export function useDisconnectKeep() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.delete('/keep/disconnect'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['keep'] });
      queryClient.invalidateQueries({ queryKey: ['lists'] });
    },
  });
}

export function useKeepLists() {
  const { data: status } = useKeepStatus();

  return useQuery({
    queryKey: ['keep', 'lists'],
    queryFn: () => api.get<KeepList[]>('/keep/lists'),
    enabled: status?.connected === true,
  });
}

export function useKeepSources() {
  const { data: status } = useKeepStatus();

  return useQuery({
    queryKey: ['keep', 'sources'],
    queryFn: () => api.get<KeepSource[]>('/keep/sources'),
    enabled: status?.connected === true,
  });
}

export function useEnableKeepSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      keepListId: string;
      localListId?: string;
      syncDirection?: 'bidirectional' | 'keep_to_local' | 'local_to_keep';
    }) => api.post<KeepSource>('/keep/sync/enable', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['keep'] });
      queryClient.invalidateQueries({ queryKey: ['lists'] });
    },
  });
}

export function useTriggerKeepSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sourceId: string) => api.post(`/keep/sync/${sourceId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['keep'] });
      queryClient.invalidateQueries({ queryKey: ['lists'] });
    },
  });
}

export function useSyncAllKeep() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post('/keep/sync/all'),
    onSuccess: () => {
      // Invalidate lists to refresh data after background sync completes
      queryClient.invalidateQueries({ queryKey: ['lists'] });
      queryClient.invalidateQueries({ queryKey: ['keep'] });
    },
  });
}

export function useDisableKeepSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sourceId: string) => api.delete(`/keep/sync/${sourceId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['keep'] });
      queryClient.invalidateQueries({ queryKey: ['lists'] });
    },
  });
}

// Notification settings types
interface NotificationSettings {
  pushEnabled: boolean;
  eventReminderMinutes: number[];
  choreReminders: boolean;
  dailyBriefingEnabled: boolean;
  dailyBriefingTime: string;
  travelTimeAlerts: boolean;
  audioAlerts: boolean;
}

interface DisplaySettings {
  startHour: number;
  endHour: number;
  schoolStartHour: number;
  schoolEndHour: number;
  showWeekends: boolean;
  defaultView: 'day' | 'week' | 'month';
  timeFormat: '12h' | '24h';
  weekStartsOn: 0 | 1; // 0=Sunday, 1=Monday
  kioskMode: boolean;
  defaultListId?: string | null; // List to open by default on Lists page
  theme?: 'light' | 'dark'; // Color theme preference
}

interface AllSettings {
  notifications: NotificationSettings;
  display: DisplaySettings;
}

interface IntegrationsStatus {
  google: { connected: boolean; calendarCount: number };
  microsoft: { connected: boolean; calendarCount: number };
  ics: { sourceCount: number };
  googleKeep: { connected: boolean };
}

interface PushSubscriptionInfo {
  id: string;
  userAgent: string | null;
  createdAt: string;
}

// Settings hooks
export function useSettings() {
  return useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<AllSettings>('/settings'),
  });
}

export function useNotificationSettings() {
  const { data: settings, ...rest } = useSettings();
  return {
    ...rest,
    data: settings?.notifications,
  };
}

export function useUpdateNotificationSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<NotificationSettings>) =>
      api.patch<NotificationSettings>('/settings/notifications', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });
}

export function useDisplaySettings() {
  const { data: settings, ...rest } = useSettings();
  return {
    ...rest,
    data: settings?.display,
  };
}

export function useUpdateDisplaySettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<DisplaySettings>) =>
      api.patch<{ message: string } & Partial<DisplaySettings>>('/settings/display', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });
}

export function useIntegrationsStatus() {
  return useQuery({
    queryKey: ['settings', 'integrations'],
    queryFn: () => api.get<IntegrationsStatus>('/settings/integrations'),
  });
}

export interface ReconnectAccount {
  provider: string;
  email: string;
  reason: string;
  members: string[];
}

export interface ReconnectStatus {
  needsReconnect: boolean;
  accounts: ReconnectAccount[];
}

/**
 * Feature flag for the calendar reconnect check. When false, the recurring
 * reconnect-status poll is disabled and the sidebar reconnect warning never
 * shows. Flip back to true to re-enable (no other changes needed).
 */
export const RECONNECT_CHECK_ENABLED = false;

/**
 * Which family calendars are broken and need the owner to reconnect.
 * Drives the reconnect warning next to the Settings button. Re-checks
 * periodically so the warning appears/clears without a page reload.
 *
 * Currently DISABLED via RECONNECT_CHECK_ENABLED: with `enabled: false` the
 * query never fires (no recurring poll, no request) and returns no data, so
 * <ReconnectWarning/> renders nothing.
 */
export function useReconnectStatus() {
  return useQuery({
    queryKey: ['settings', 'reconnect-status'],
    queryFn: () => api.get<ReconnectStatus>('/settings/integrations/reconnect-status'),
    enabled: RECONNECT_CHECK_ENABLED,
    refetchInterval: RECONNECT_CHECK_ENABLED ? 5 * 60 * 1000 : false,
    refetchOnWindowFocus: RECONNECT_CHECK_ENABLED,
    staleTime: 60 * 1000,
  });
}

// Push notification hooks
export function useVapidPublicKey() {
  return useQuery({
    queryKey: ['push', 'vapid'],
    queryFn: () => api.get<{ publicKey: string }>('/push/vapid-public-key'),
    retry: false,
    staleTime: Infinity,
  });
}

export function usePushStatus() {
  return useQuery({
    queryKey: ['push', 'status'],
    queryFn: () => api.get<{ enabled: boolean }>('/push/status'),
    staleTime: Infinity,
  });
}

export function usePushSubscriptions() {
  return useQuery({
    queryKey: ['push', 'subscriptions'],
    queryFn: () => api.get<{ subscriptions: PushSubscriptionInfo[] }>('/push/subscriptions'),
  });
}

export function usePushSubscribe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
      api.post<{ subscriptionId: string; message: string }>('/push/subscribe', subscription),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['push', 'subscriptions'] });
    },
  });
}

export function usePushUnsubscribe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (endpoint: string) => api.delete<{ message: string }>('/push/unsubscribe', { endpoint }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['push', 'subscriptions'] });
    },
  });
}

export function usePushTest() {
  return useMutation({
    mutationFn: () => api.post<{ message: string; sent: number; failed: number }>('/push/test'),
  });
}

// Daily briefing types
interface DailyBriefing {
  events: number;
  chores: number;
  content: string | null;
  date: string;
}

// Daily briefing hooks
export function useDailyBriefing() {
  return useQuery({
    queryKey: ['dailyBriefing'],
    queryFn: () => api.get<DailyBriefing>('/push/daily-briefing'),
    staleTime: 5 * 60 * 1000, // Consider stale after 5 minutes
  });
}

export function useSendDailyBriefing() {
  return useMutation({
    mutationFn: () => api.post<{ message: string; sent: number; failed?: number }>('/push/daily-briefing/send'),
  });
}

// Invitation types
interface Invitation {
  id: string;
  familyId: string;
  memberId: string;
  inviterUserId: string | null;
  email: string;
  token: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string;
  updatedAt: string;
  member: Member;
  inviter: { id: string; name: string; email: string } | null;
}

interface InvitationResponse extends Invitation {
  emailSent: boolean;
  inviteUrl?: string;
}

interface InvitationStatus {
  emailConfigured: boolean;
}

// Invitation hooks
export function useInvitations() {
  return useQuery({
    queryKey: ['invitations'],
    queryFn: () => api.get<Invitation[]>('/invitations'),
  });
}

export function useInvitationStatus() {
  return useQuery({
    queryKey: ['invitations', 'status'],
    queryFn: () => api.get<InvitationStatus>('/invitations/status'),
    staleTime: Infinity,
  });
}

export function useCreateInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { memberId: string; email: string }) =>
      api.post<InvitationResponse>('/invitations', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      queryClient.invalidateQueries({ queryKey: ['members'] });
    },
  });
}

export function useRevokeInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete<Invitation>(`/invitations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
  });
}

// School Checklist types
export interface SchoolChecklistItem {
  id: string;
  familyId: string;
  title: string;
  category: string | null;
  daysOfWeek: number[];
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SchoolChecklistOverride extends SchoolChecklistItem {
  memberId: string;
}

export interface SchoolKeywordRule {
  id: string;
  familyId: string;
  keyword: string;
  checklistItem: string;
  category: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SchoolChecklistCheck {
  id: string;
  familyId: string;
  memberId: string;
  itemKey: string;
  checkDate: string;
  checkedAt: string;
}

export interface SchoolChecklistData {
  baseItems: SchoolChecklistItem[];
  overrides: SchoolChecklistOverride[];
  keywordRules: SchoolKeywordRule[];
  checks: SchoolChecklistCheck[];
}

// School Checklist hooks
export function useSchoolChecklist(date?: string) {
  const params = date ? `?date=${date}` : '';
  return useQuery({
    queryKey: ['school-checklist', date],
    queryFn: () => api.get<SchoolChecklistData>(`/school-checklist${params}`),
  });
}

export function useCreateSchoolChecklistItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      title: string;
      category?: string;
      daysOfWeek?: number[];
      displayOrder?: number;
    }) => api.post<SchoolChecklistItem>('/school-checklist/items', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-checklist'] });
    },
  });
}

export function useUpdateSchoolChecklistItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: {
      id: string;
      title?: string;
      category?: string;
      daysOfWeek?: number[];
      displayOrder?: number;
      isActive?: boolean;
    }) => api.patch<SchoolChecklistItem>(`/school-checklist/items/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-checklist'] });
    },
  });
}

export function useDeleteSchoolChecklistItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/school-checklist/items/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-checklist'] });
    },
  });
}

export function useCreateSchoolChecklistOverride() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      memberId: string;
      title: string;
      category?: string;
      daysOfWeek?: number[];
      displayOrder?: number;
    }) => api.post<SchoolChecklistOverride>('/school-checklist/overrides', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-checklist'] });
    },
  });
}

export function useUpdateSchoolChecklistOverride() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: {
      id: string;
      title?: string;
      category?: string;
      daysOfWeek?: number[];
      displayOrder?: number;
      isActive?: boolean;
    }) => api.patch<SchoolChecklistOverride>(`/school-checklist/overrides/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-checklist'] });
    },
  });
}

export function useDeleteSchoolChecklistOverride() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/school-checklist/overrides/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-checklist'] });
    },
  });
}

export function useCreateSchoolKeywordRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      keyword: string;
      checklistItem: string;
      category?: string;
    }) => api.post<SchoolKeywordRule>('/school-checklist/keyword-rules', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-checklist'] });
    },
  });
}

export function useDeleteSchoolKeywordRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/school-checklist/keyword-rules/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-checklist'] });
    },
  });
}

export function useToggleSchoolCheck() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      memberId: string;
      itemKey: string;
      date?: string;
    }) => api.post<{ checked: boolean; memberId: string; itemKey: string; checkDate: string }>('/school-checklist/check', data),
    retry: false,
    onMutate: async ({ memberId, itemKey, date }) => {
      const dateKey = date || new Date().toISOString().split('T')[0];
      await queryClient.cancelQueries({ queryKey: ['school-checklist'] });

      const previousData = queryClient.getQueryData<SchoolChecklistData>(['school-checklist', dateKey]);

      // Optimistic update: toggle the check
      queryClient.setQueryData<SchoolChecklistData>(['school-checklist', dateKey], (old) => {
        if (!old) return old;
        const existingCheck = old.checks.find(
          (c) => c.memberId === memberId && c.itemKey === itemKey
        );
        if (existingCheck) {
          // Remove check (uncheck)
          return {
            ...old,
            checks: old.checks.filter((c) => c.id !== existingCheck.id),
          };
        } else {
          // Add check
          return {
            ...old,
            checks: [
              ...old.checks,
              {
                id: `temp-${Date.now()}`,
                familyId: '',
                memberId,
                itemKey,
                checkDate: dateKey!,
                checkedAt: new Date().toISOString(),
              },
            ],
          };
        }
      });

      return { previousData, dateKey };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData && context?.dateKey) {
        queryClient.setQueryData<SchoolChecklistData>(
          ['school-checklist', context.dateKey],
          context.previousData
        );
      }
    },
    onSuccess: (_data, _variables, _context) => {
      // Delay invalidation slightly to ensure the server has committed
      // and to avoid overwriting the optimistic update prematurely
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['school-checklist'] });
      }, 500);
    },
  });
}

// Pack Bags types and hook
interface PackBagsMemberItem {
  key: string;
  title: string;
  category: string | null;
  source: string;
}

interface PackBagsMember {
  memberId: string;
  memberName: string;
  items: PackBagsMemberItem[];
}

export interface PackBagsData {
  date: string;
  members: PackBagsMember[];
}

export function usePackBags(date?: string) {
  const params = date ? `?date=${date}` : '';
  return useQuery({
    queryKey: ['pack-bags', date],
    queryFn: () => api.get<PackBagsData>(`/school-checklist/pack-bags${params}`),
    enabled: !!date,
  });
}
